import React, { useState, useEffect } from 'react';
import { FiX, FiLogIn, FiLogOut, FiClock, FiCalendar, FiUser } from 'react-icons/fi';
import { userRequest } from '../utils/requestMethods';
import { format, parseISO } from 'date-fns';
import { toast } from 'react-toastify';

const StudentClockInHistoryModal = ({ isOpen, onClose, student, schoolId, termStartDate, termEndDate }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalDays: 0,
    totalClockIns: 0,
    totalClockOuts: 0,
    averageDuration: 0,
  });

  useEffect(() => {
    if (isOpen && student && schoolId) {
      fetchStudentHistory();
    }
  }, [isOpen, student, schoolId]);

  const fetchStudentHistory = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        student_id: student.student_id,
        from: termStartDate,
        to: termEndDate,
        limit: '100',
        order: 'desc',
        sort_by: 'date',
      });

      const { data } = await userRequest.get(`/children/${schoolId}/clock-in-history?${params}`);
      
      const studentRecords = data.records || [];
      setRecords(studentRecords);
      calculateStats(studentRecords);
    } catch (error) {
      console.error('Error fetching student clock-in history:', error);
      toast.error('Failed to load student history');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (recordsData) => {
    const totalClockIns = recordsData.filter(r => r.clock_in).length;
    const totalClockOuts = recordsData.filter(r => r.clock_out).length;
    
    // Calculate average duration for records with both clock in and out
    const completedRecords = recordsData.filter(r => r.clock_in && r.clock_out);
    let averageDuration = 0;
    
    if (completedRecords.length > 0) {
      const totalDuration = completedRecords.reduce((sum, record) => {
        const clockIn = new Date(record.clock_in.timestamp);
        const clockOut = new Date(record.clock_out.timestamp);
        return sum + (clockOut - clockIn);
      }, 0);
      
      averageDuration = totalDuration / completedRecords.length;
    }

    setStats({
      totalDays: recordsData.length,
      totalClockIns,
      totalClockOuts,
      averageDuration,
    });
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      return format(parseISO(timestamp), 'hh:mm a');
    } catch {
      return 'Invalid time';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(parseISO(dateString), 'EEE, MMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  const formatDuration = (ms) => {
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  const getDurationInMs = (clockIn, clockOut) => {
    if (!clockIn || !clockOut) return 0;
    const clockInTime = new Date(clockIn.timestamp);
    const clockOutTime = new Date(clockOut.timestamp);
    return clockOutTime - clockInTime;
  };

  const getStatusColor = (status) => {
    return status === 'clocked_in' 
      ? 'bg-green-100 text-green-800 border-green-200' 
      : 'bg-gray-100 text-gray-800 border-gray-200';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gray-100 p-6 text-slate-500">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                  <FiUser className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">{student?.student_name}</h2>
                  <p className="text-slate-500 text-sm">{student?.class_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                <FiCalendar className="w-4 h-4" />
                <span>
                  {format(parseISO(termStartDate), 'MMM dd, yyyy')} - {format(parseISO(termEndDate), 'MMM dd, yyyy')}
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-slate-500 bg-gray-200 p-2 rounded-lg transition-colors"
            >
              <FiX className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="p-6 bg-slate-50 border-b border-slate-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FiCalendar className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Total Days</p>
                  <p className="text-xl font-bold text-slate-800">{stats.totalDays}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <FiLogIn className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Clock Ins</p>
                  <p className="text-xl font-bold text-slate-800">{stats.totalClockIns}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                  <FiLogOut className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Clock Outs</p>
                  <p className="text-xl font-bold text-slate-800">{stats.totalClockOuts}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <FiClock className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Avg Duration</p>
                  <p className="text-lg font-bold text-slate-800">
                    {stats.averageDuration > 0 ? formatDuration(stats.averageDuration) : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Records List */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 320px)' }}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-200 border-t-indigo-600"></div>
            </div>
          ) : records.length > 0 ? (
            <div className="space-y-3">
              {records.map((record, index) => {
                const duration = getDurationInMs(record.clock_in, record.clock_out);
                
                return (
                  <div
                    key={record._id || index}
                    className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      {/* Date Section */}
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 bg-indigo-50 rounded-lg flex flex-col items-center justify-center">
                          <span className="text-xs text-indigo-600 font-medium">
                            {format(parseISO(record.date), 'MMM')}
                          </span>
                          <span className="text-lg font-bold text-indigo-700">
                            {format(parseISO(record.date), 'dd')}
                          </span>
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800">
                            {formatDate(record.date)}
                          </p>
                          <p className="text-xs text-slate-500">
                            Week {record.week}, Day {record.day}
                          </p>
                        </div>
                      </div>

                      {/* Clock In/Out Times */}
                      <div className="flex items-center gap-6">
                        {/* Clock In */}
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                            <FiLogIn className="w-4 h-4 text-green-600" />
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Clock In</p>
                            <p className="font-semibold text-sm text-green-700">
                              {formatTime(record.clock_in?.timestamp)}
                            </p>
                          </div>
                        </div>

                        {/* Arrow */}
                        {record.clock_out && (
                          <div className="hidden md:block text-slate-300">→</div>
                        )}

                        {/* Clock Out */}
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                            <FiLogOut className="w-4 h-4 text-gray-600" />
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Clock Out</p>
                            <p className="font-semibold text-sm text-gray-700">
                              {record.clock_out ? formatTime(record.clock_out.timestamp) : (
                                <span className="text-xs text-slate-400">Not yet</span>
                              )}
                            </p>
                          </div>
                        </div>

                        {/* Duration */}
                        {duration > 0 && (
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                              <FiClock className="w-4 h-4 text-blue-600" />
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Duration</p>
                              <p className="font-semibold text-sm text-blue-700">
                                {formatDuration(duration)}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Status Badge */}
                        <div>
                          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(record.status)}`}>
                            {record.status === 'clocked_in' ? (
                              <>
                                <FiLogIn className="w-3 h-3" />
                                In School
                              </>
                            ) : (
                              <>
                                <FiLogOut className="w-3 h-3" />
                                Left
                              </>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Scanned By Information */}
                    {(record.clock_in?.marked_by || record.clock_out?.marked_by) && (
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <div className="flex flex-wrap gap-4 text-xs text-slate-500">
                          {record.clock_in?.marked_by && (
                            <div>
                              <span className="font-medium">Clock In by:</span>{' '}
                              {record.clock_in.marked_by.name}
                            </div>
                          )}
                          {record.clock_out?.marked_by && (
                            <div>
                              <span className="font-medium">Clock Out by:</span>{' '}
                              {record.clock_out.marked_by.name}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="mx-auto h-16 w-16 text-slate-300 mb-4 flex items-center justify-center">
                <FiClock className="w-12 h-12" />
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-2">No Records Found</h3>
              <p className="text-slate-500 text-sm">
                No clock in/out records for this student in the current term
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-slate-50 border-t border-slate-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-slate-600 hover:bg-slate-700 text-white font-medium rounded-full transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudentClockInHistoryModal;

